import React, { useMemo } from 'react';
import { useUsersData } from '../hooks/useUsersData';

const UsersList = () => {
  const { users, loading, error, refresh } = useUsersData();

  const tableHeaders = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'username', label: 'Username' },
      { key: 'age', label: 'Age' },
      { key: 'gender', label: 'Gender' },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-lg bg-white shadow">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">User Directory</h1>
            <p className="mt-1 text-sm text-slate-500">Powered by the DummyJSON Users API.</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow transition focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            {loading ? 'Refreshing…' : 'Refresh Data'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700">
            <thead className="bg-slate-50">
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    key={header.key}
                    scope="col"
                    className="px-4 py-3 font-semibold uppercase tracking-wide text-slate-600"
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={tableHeaders.length} className="px-4 py-6 text-center text-sm text-slate-600">
                    <div className="flex items-center justify-center gap-3">
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
                      <span>Loading users…</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan={tableHeaders.length} className="px-4 py-8 text-center">
                    <div className="mx-auto max-w-md space-y-3">
                      <p className="text-base font-semibold text-red-600">We hit a snag loading users.</p>
                      <p className="text-sm text-slate-600">
                        {error.message || 'Something went wrong while contacting the server.'}
                      </p>
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={refresh}
                          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && !error && users.length === 0 && (
                <tr>
                  <td colSpan={tableHeaders.length} className="px-4 py-8 text-center text-sm text-slate-500">
                    No users found. Try refreshing the data.
                  </td>
                </tr>
              )}

              {!loading && !error && users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">{user.id}</td>
                  <td className="whitespace-nowrap px-4 py-3">{user.firstName}</td>
                  <td className="whitespace-nowrap px-4 py-3">{user.lastName}</td>
                  <td className="px-4 py-3 text-slate-600">{user.email}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{user.username}</td>
                  <td className="whitespace-nowrap px-4 py-3">{user.age}</td>
                  <td className="whitespace-nowrap px-4 py-3 capitalize">{user.gender}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <footer className="mx-auto mt-8 max-w-6xl rounded-lg border border-dashed border-slate-300 bg-slate-200/60 px-6 py-4 text-center text-sm text-slate-600">
        INF651 - Front End Development, AUPP
      </footer>
    </div>
  );
};

export default UsersList;
